export const dateFormatList = ["MM/DD/YYYY"];

// Base configuration for common chip filters. "All" includes everything,
// approved items too — senders need to see documents that came back
// approved (Matthew, 2026-07-21). The trailing "Reviewed" chip is a
// shortcut to just the approved subset; filtering happens server-side.
const baseChipConfig = [
  { key: "ALL", countKey: "all_count", label: "All" },
  { key: "OVERDUE", countKey: "overdue_count", label: "Overdue" },
  { key: "DUE_SOON", countKey: "due_soon_count", label: "Due Soon" },
  { key: "PENDING", countKey: "pending_count", label: "Review Pending" },
];

const reviewedChip = {
  key: "REVIEWED",
  countKey: "reviewed_count",
  label: "Reviewed",
};

const simpleChipConfig = [
  { key: "ALL", countKey: "all_count", label: "All" },
  { key: "PENDING", countKey: "pending_count", label: "Pending" },
];

const veteransAffairsChipConfig = [
  { key: "ALL", countKey: "all", label: "All" },
  { key: "PENDING", countKey: "pending", label: "Pending" },
  { key: "ERROR", countKey: "error", label: "Error" },
];

// Tasks / Assignments chips key straight off the backend's `chip_counts`, whose
// keys are the chip values themselves rather than the `*_count` names
// the review endpoints use. "Active" leads because it is the default
// and the question both tabs exist to answer.
const workItemsChipConfig = [
  { key: "ACTIVE", countKey: "ACTIVE", label: "Active" },
  { key: "OVERDUE", countKey: "OVERDUE", label: "Overdue" },
  { key: "OPEN", countKey: "OPEN", label: "Not Started" },
  { key: "IN_PROGRESS", countKey: "IN_PROGRESS", label: "In Progress" },
  { key: "COMPLETED", countKey: "COMPLETED", label: "Complete" },
  { key: "ALL", countKey: "ALL", label: "All" },
];

export const chipFilterConfig = {
  myReview: [...baseChipConfig, reviewedChip],
  sharedReview: [
    ...baseChipConfig,
    {
      key: "REWORK_REQUESTED",
      countKey: "rework_requested_count",
      label: "Rework Requested",
    },
    reviewedChip,
  ],
  needsMySignature: simpleChipConfig,
  // Sent requests: the backend's stats carry completed_count and
  // cancelled_count (cancelled, expired, declined or failed) as well.
  sentForSignature: [
    ...simpleChipConfig,
    { key: "COMPLETED", countKey: "completed_count", label: "Completed" },
    { key: "CANCELLED", countKey: "cancelled_count", label: "Cancelled" },
  ],
  veteransAffairs: veteransAffairsChipConfig,
  tasks: workItemsChipConfig,
  assignments: workItemsChipConfig,
  messages: [
    { key: "all", countKey: "all", label: "All" },
    { key: "unread", countKey: "unread", label: "Unread" },
    { key: "sent", countKey: "sent", label: "Sent" },
  ],
};

export const reviewStatusConfig = [
  { value: "PENDING", label: "Review Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REWORK", label: "Rework Requested" },
  { value: "ALL", label: "All" },
];

export const signatureStatusConfig = [
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "ALL", label: "All" },
];

export const veteransAffairsStatusConfig = [
  { value: "PENDING", label: "Pending" },
  { value: "UPLOADED", label: "Uploaded" },
  { value: "RECEIVED", label: "Received" },
  { value: "PROCESSING", label: "Processing" },
  { value: "SUCCESS", label: "Success" },
  { value: "VBMS", label: "VBMS" },
  { value: "ERROR", label: "Error" },
  { value: "EXPIRED", label: "Expired" },
  { value: "ALL", label: "All" },
];

export const dueOnOptions = [
  { value: "TODAY", label: "Today" },
  { value: "TOMORROW", label: "Tomorrow" },
  { value: "IN_7_DAYS", label: "In 7 Days" },
  { value: "OVERDUE", label: "Overdue" },
  { value: "ALL", label: "All" },
];

export const MAX_PASSWORD_LENGTH = 50;
export const MAX_EMAIL_LENGTH = 320;
export const MAX_ROLE_LENGTH = 50;
export const MAX_NAME_LENGTH = 90;
export const MAX_SIZE_BYTES = 100 * 1024 * 1024;

export const DISABLE_AUTOSAVE_PLACEHOLDERS = [
  "Rename",
  "Search",
  "Add Recipient",
  "Add note to review..",
  "Enter password",
  "Enter email",
  "Name",
  "Role",
  "Document Signature Request",
  "Add comments (Optional)",
];


export const ERROR_MESSAGES = {
  // Shared
  REQUIRED_PASSWORD: "Password is required", // NOSONAR
  INVALID_PASSWORD:
    "Password must be at least 8 characters long and include uppercase and lowercase letters, a number, and a special character.", // NOSONAR
  REQUIRED_EXPIRY: "Expiry date is required",
  REQUIRED_EMAIL: "At least one email is required",
  INVALID_EMAIL: "Invalid email(s)",
  EMAIL_TOO_LONG: (limit) => `Email(s) exceeding ${limit} characters`,
  PASSWORD_TOO_LONG: (limit) => `Password must not exceed ${limit} characters`,

  // Recipient validation
  REQUIRED_NAME: "Name is required",
  NAME_TOO_LONG: (limit) => `Name must not exceed ${limit} characters`,
  INDIVIDUAL_EMAIL_REQUIRED: "Email is required",
  INDIVIDUAL_EMAIL_TOO_LONG: (limit) => `Email must not exceed ${limit} characters`,
  INDIVIDUAL_EMAIL_INVALID: "Valid email is required",
  REQUIRED_ROLE: "Role is required",
  ROLE_TOO_LONG: (limit) => `Role must not exceed ${limit} characters`,
  REQUIRED_PHONE: "Phone number is required for SMS delivery",
  INVALID_PHONE: "Enter a valid 10-digit US phone number",
  CATEGORY_NAME_EMPTY: "Category name cannot be empty",
  CATEGORY_NAME_MAX_LENGTH: "Category name cannot exceed 50 characters",
};

export const PAGE_SIZE_OPTIONS = [
  {
    value: "10",
    label: "10",
  },
  {
    value: "25",
    label: "25",
  },
  {
    value: "50",
    label: "50",
  },
  {
    value: "100",
    label: "100",
  },
];

export const MAX_VISIBLE_TABS = 10;

export const PDF_FILE_TYPES = ["pdf", "application/pdf"];
export const EMAIL_FILE_TYPES = [
  "eml",
  "msg",
  "message/rfc822",
  "application/vnd.ms-outlook",
];

export const DOC_FILE_TYPES = [
  "docx",
  "doc",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const XLSX_FILE_TYPES = [
  "xlsx",
  "xls",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// What the regular Upload File(s) picker offers. Matches exactly what the
// upload lane accepts (processFiles: PDF + MS Office + read-only email files;
// Office files route to SharePoint server-side). Keep this in sync with the
// backend's ALLOWED_EXTENSIONS.
export const UPLOAD_PICKER_ACCEPT = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".eml",
  ".msg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "message/rfc822",
  "application/vnd.ms-outlook",
].join(",");

export const IMAGE_FILE_TYPES = [
  "png",
  "jpg",
  "jpeg",
  "jfif",
  "tif",
  "tiff",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/jfif",
  "image/tiff",
];

// Native browser media previews. The browser still decides whether a
// particular codec is playable; unsupported combinations get a download
// fallback instead of an empty viewer pane.
export const AUDIO_FILE_TYPES = [
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "oga",
  "flac",
  "audio/mpeg",
  "audio/wav",
  "audio/x-wav",
  "audio/mp4",
  "audio/aac",
  "audio/ogg",
  "audio/flac",
];

export const VIDEO_FILE_TYPES = [
  "mp4",
  "webm",
  "ogv",
  "ogg",
  "mov",
  "avi",
  "mkv",
  "mpeg",
  "mpg",
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",
  "video/x-msvideo",
  "video/x-matroska",
  "video/mpeg",
];

export const MS_FILE_TYPES = [...DOC_FILE_TYPES, ...XLSX_FILE_TYPES];

// My Files (personal storage) only, read in-app via react-reader/epub.js —
// see src/pages/MyFiles/EpubReader.js. Not part of UPLOAD_PICKER_ACCEPT or
// the matter upload gate; personal storage has no extension whitelist.
export const EPUB_FILE_TYPES = ["epub", "application/epub+zip"];

export const mimeToExtension = {
  "application/epub+zip": "epub",
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/jfif": "jfif",
  "image/tiff": "tiff",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/mp4": "m4a",
  "audio/aac": "aac",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/ogg": "ogv",
  "video/quicktime": "mov",
  "video/x-msvideo": "avi",
  "video/x-matroska": "mkv",
  "video/mpeg": "mpeg",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/msword": "docx",
  "text/plain": "txt",
  "pdf": "pdf",
  "docx":"docx",
  "xlsx":"xlsx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":"xlsx",
  "application/vnd.ms-outlook":"msg",
  "message/rfc822": "eml",
};
